import camelCase from 'lodash/camelCase'
import { Durations } from '@/constants/durations'
import { TranslateFunction } from '@/utils/translate/translate-client'
import { PaymentStatus, Perk, Subscription } from './PaymentsService'

export type Obj = unknown[] | Record<string, unknown>

export function isActiveSubscription(subscription: Subscription) {
  return (
    subscription.status === PaymentStatus.Completed ||
    subscription.status === PaymentStatus.Active ||
    (subscription.status === PaymentStatus.Incomplete && !subscription.CanceledAt)
  )
}

export function isCancelledSubscription(subscription: Subscription) {
  return subscription.status === PaymentStatus.Canceled
}

export const isGuildSubscription = (subscription?: Subscription) => {
  if (!subscription) return false

  const regexTest = /guild/i
  return (
    regexTest.test(subscription.name) ||
    // TODO: rm; in real life, I don't think this is ever 'guild'. It's always 'angel'; Anyone know better?
    regexTest.test(subscription.project.slug) ||
    regexTest.test(subscription.project.title)
  )
}

export const isRecentSubscription = (subscription?: Subscription, numberOfDays = 30) => {
  if (!subscription) return false

  const now = new Date()
  const subscriptionDate = new Date(subscription.created)
  const diff = Math.abs(now.getTime() - subscriptionDate.getTime())
  const diffDays = Math.ceil(diff / (1000 * 3600 * 24))
  return diffDays <= numberOfDays
}

export const extractPerks = (subscription?: Subscription, active = true) => {
  if (isGuildSubscription(subscription)) {
    return [
      'Vote on which shows get made next',
      'Help spread the shows you love to more people',
      'Receive early access to select shows',
    ]
  }

  let filteredProducts = []
  const offerSnapshot = subscription?.offerSnapshot

  if (offerSnapshot) {
    const offeredProducts = JSON.parse(offerSnapshot).offeredproduct_set
    filteredProducts = active ? offeredProducts.filter((perk: Perk) => perk.product.active) : offeredProducts
  }

  return filteredProducts.map((perk: Perk) => perk.product.name)
}

export const GUILD_CANCEL_EVENT_NAME = 'Guild Subscription Cancellation'

export function getCancellationEventPrefix(subscription?: Subscription) {
  if (isGuildSubscription(subscription)) {
    return GUILD_CANCEL_EVENT_NAME
  }
  return 'PIF Subscription Cancellation'
}

/**
 * Gets sub end date or uses the current date.
 * TODO: I see the current date fallback everywhere but it doesn't make sense to me. Verify it's correct.
 */
export function getEndDate(subscription: Subscription, today = new Date()): Date {
  return subscription.end ? new Date(subscription.end) : today
}

export function getDaysRemaining(subscription: Subscription, today = new Date()) {
  return Math.floor((getEndDate(subscription, today).getTime() - today.getTime()) / Durations.ONE_DAY_IN_SECONDS / 1000)
}
export function getIntervalLabel(t: TranslateFunction, interval: 'month' | 'year'): string {
  return interval === 'month' ? t('monthV2', 'month') : t('yearV2', 'year')
}

export const parseObject: (val: Obj, fn: (val: string) => string) => Obj = (val, fn) => {
  if (Array.isArray(val)) {
    return val.map((v) => (v && typeof v === 'object' ? parseObject(v as Obj, fn) : v))
  }

  if (typeof val !== 'object' || val === null || val === undefined) {
    return val
  }

  const newObj: Obj = {}

  for (const d in val) {
    if (Object.prototype.hasOwnProperty.call(val, d)) {
      newObj[fn(d)] = val[d] && typeof val[d] === 'object' ? parseObject(val[d] as Obj, fn) : val[d]
    }
  }

  return newObj
}

export const snakeToCamel = (obj: Obj) => parseObject(obj, camelCase)
